// Spec §9.4 / Phase B2 — inline SizeDefinition manager. Opens from a
// row action on the SizeScale tab, lets the coordinator add / edit /
// delete the definitions (1S, 3S, 6S, 20S etc.) attached to that scale
// without leaving the settings page.

import { useState } from "react"
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { useTranslation } from "react-i18next"
import { CheckIcon, PencilSimpleIcon, PlusIcon, TrashIcon, XIcon } from "@phosphor-icons/react"
import RenderText from "../ui/render-text"
import type { ICreateSizeDefinitionInput, ISizeDefinition, ISizeScale } from "../../apis/size-scale"
import { useSettingsMutations, useSizeDefinitionsQuery } from "./hooks"
import classes from "./settings-modal.module.css"

type Props = {
  opened: boolean
  onClose: () => void
  scale: ISizeScale | null
}

type FormShape = {
  code: string
  label: string
  numericValue: string | number
  sortOrder: number
}

function emptyForm (): FormShape {
  return { code: "", label: "", numericValue: 1, sortOrder: 0 }
}

export function SizeDefinitionsModal ({ opened, onClose, scale }: Props) {
  const { t } = useTranslation("settings")
  const sizeScaleId = scale?.id ?? null
  const { rows, isLoading } = useSizeDefinitionsQuery(opened ? sizeScaleId : null)
  const mutations = useSettingsMutations()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const form = useForm<FormShape>({
    initialValues: emptyForm(),
    validate     : {
      code        : (v) => (!v.trim() ? t("sizeScale.definitions.validation.codeRequired") : null),
      label       : (v) => (!v.trim() ? t("sizeScale.definitions.validation.labelRequired") : null),
      numericValue: (v) => (Number(v) <= 0 ? t("sizeScale.definitions.validation.valuePositive") : null),
    },
  })

  const close = () => {
    setEditingId(null)
    setShowCreate(false)
    form.reset()
    onClose()
  }

  const startEdit = (def: ISizeDefinition) => {
    setEditingId(def.id)
    setShowCreate(false)
    form.setValues({
      code        : def.code,
      label       : def.label,
      numericValue: Number(def.numericValue),
      sortOrder   : def.sortOrder,
    })
  }

  const startCreate = () => {
    setEditingId(null)
    setShowCreate(true)
    form.setValues(emptyForm())
  }

  const cancelEdit = () => {
    setEditingId(null)
    setShowCreate(false)
    form.reset()
  }

  const submit = form.onSubmit(async (values) => {
    if (!sizeScaleId) return
    const payload: ICreateSizeDefinitionInput = {
      code        : values.code.trim(),
      label       : values.label.trim(),
      numericValue: values.numericValue,
      sortOrder   : values.sortOrder,
    }
    if (editingId) {
      await mutations.sizeDefinition.update(sizeScaleId, editingId, payload)
    } else {
      await mutations.sizeDefinition.create(sizeScaleId, payload)
    }
    cancelEdit()
  })

  const handleDelete = (def: ISizeDefinition) => {
    if (!sizeScaleId) return
    modals.openConfirmModal({
      title       : t("common.delete"),
      children    : (
        <Text size="sm">
          {t("common.confirmDelete", { name: def.code })}
        </Text>
      ),
      labels      : { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm   : () => mutations.sizeDefinition.remove(sizeScaleId, def.id),
    })
  }

  const modalTitle = scale
    ? t("sizeScale.definitions.modalTitle", { scaleName: scale.name })
    : t("sizeScale.definitions.modalTitleFallback")

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={modalTitle}
      size="lg"
      radius="lg"
      classNames={{ title: classes.title, close: classes.close }}
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t("sizeScale.definitions.count", { count: rows.length })}
          </Text>
          {!showCreate && !editingId && (
            <Button size="sm" leftSection={<PlusIcon size={14} />} onClick={startCreate}>
              {t("sizeScale.definitions.newDefinition")}
            </Button>
          )}
        </Group>

        <Table withTableBorder striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={120}>{t("sizeScale.definitions.columns.code")}</Table.Th>
              <Table.Th>{t("sizeScale.definitions.columns.label")}</Table.Th>
              <Table.Th w={120}>{t("sizeScale.definitions.columns.value")}</Table.Th>
              <Table.Th w={80}>{t("sizeScale.definitions.columns.sortOrder")}</Table.Th>
              <Table.Th w={80} ta="center">{t("common.actions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <RenderText text={t("common.loading")} color="var(--mantine-color-dimmed)" py="md" />
                </Table.Td>
              </Table.Tr>
            )}
            {!isLoading && rows.length === 0 && !showCreate && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <RenderText text={t("sizeScale.definitions.empty")} color="var(--mantine-color-dimmed)" py="md" />
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((def) => editingId === def.id ? (
              <Table.Tr key={def.id} bg="var(--mantine-color-blue-0)">
                <Table.Td><TextInput {...form.getInputProps("code")} size="sm" /></Table.Td>
                <Table.Td><TextInput {...form.getInputProps("label")} size="sm" /></Table.Td>
                <Table.Td>
                  <NumberInput
                    {...form.getInputProps("numericValue")}
                    size="sm"
                    min={0}
                    decimalScale={4}
                    hideControls
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput {...form.getInputProps("sortOrder")} size="sm" min={0} hideControls />
                </Table.Td>
                <Table.Td ta="center">
                  <Group gap={4} justify="center" wrap="nowrap">
                    <Tooltip label={t("common.save")} withArrow>
                      <ActionIcon
                        variant="filled"
                        color="teal"
                        size="sm"
                        onClick={() => submit()}
                        loading={mutations.submitting}
                      >
                        <CheckIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("common.cancel")} withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" onClick={cancelEdit}>
                        <XIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={def.id}>
                <Table.Td><RenderText text={def.code} fontWeight={600} /></Table.Td>
                <Table.Td><RenderText text={def.label} /></Table.Td>
                <Table.Td>
                  <RenderText text={String(def.numericValue)} />
                </Table.Td>
                <Table.Td><RenderText text={String(def.sortOrder)} /></Table.Td>
                <Table.Td ta="center">
                  <Group gap={4} justify="center" wrap="nowrap">
                    <Tooltip label={t("common.edit")} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        size="sm"
                        onClick={() => startEdit(def)}
                        disabled={!!editingId || showCreate}
                      >
                        <PencilSimpleIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("common.delete")} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleDelete(def)}
                        disabled={!!editingId || showCreate}
                      >
                        <TrashIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {showCreate && (
              <Table.Tr bg="var(--mantine-color-green-0)">
                <Table.Td><TextInput {...form.getInputProps("code")} size="sm" placeholder="3S" /></Table.Td>
                <Table.Td><TextInput {...form.getInputProps("label")} size="sm" placeholder="3 Size" /></Table.Td>
                <Table.Td>
                  <NumberInput
                    {...form.getInputProps("numericValue")}
                    size="sm"
                    min={0}
                    decimalScale={4}
                    hideControls
                    placeholder="3"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput {...form.getInputProps("sortOrder")} size="sm" min={0} hideControls />
                </Table.Td>
                <Table.Td ta="center">
                  <Group gap={4} justify="center" wrap="nowrap">
                    <Tooltip label={t("common.create")} withArrow>
                      <ActionIcon
                        variant="filled"
                        color="teal"
                        size="sm"
                        onClick={() => submit()}
                        loading={mutations.submitting}
                      >
                        <CheckIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("common.cancel")} withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" onClick={cancelEdit}>
                        <XIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end" className={classes.footer}>
          <Button variant="default" onClick={close}>{t("common.close")}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
